import { Injectable, inject } from '@angular/core';
import { InMemoryHttpClient } from './in-memory-http-client';
import { Observable } from 'rxjs';
import { RequestInfo, ResponseOptions, STATUS } from 'angular-in-memory-web-api';
import { TestEmployeeDataService } from './test-employee-data.service';
import {
  AddEmployeeAvailabilityRequest,
  AddEmployeeCapabilityRequest,
  AddEmployeeManagerSettingsRequest,
  AddEmployeeMaximumWorkHoursRequest,
  AddEmployeeOneTimePreferredTimeOffRequest,
  AddEmployeeRankRequest,
  AddEmployeeWeeklyPreferredTimeOffRequest,
  EditEmployeeAvailabilityRequest,
  EditEmployeeCapabilityRequest,
  EditEmployeeManagerSettingsRequest,
  EditEmployeeMaximumWorkHoursRequest,
  EditEmployeeOneTimePreferredTimeOffRequest,
  EditEmployeeRankRequest,
  EditEmployeeWeeklyPreferredTimeOffRequest
} from '../../../interfaces/request/EmployeeRequests';
import { StoreEmployeeAvailabilityResponse } from '../../../interfaces/response/EmployeeResponse';

/**
 * This service intercepts {@link EmployeeService} requests and redirects them to an in-memory data store.
 */
@Injectable({
  providedIn: 'root',
})
export class InMemoryEmployeeDataService implements InMemoryHttpClient {
  private testEmployeeDataService = inject(TestEmployeeDataService);


  /**
   * Intercepts HTTP DELETE request of {@link EmployeeService}. Deletes a record from an in-memory data store.
   * @param reqInfo information about the current HTTP DELETE request.
   */
  delete(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      if (reqInfo.req.url.includes('maximum-work-hours')) {
        const date = reqInfo.req.url.split('maximum-work-hours/')[1];
        return this.deleteMaximumWorkingHours(date);
      }
      if (reqInfo.req.url.includes('one-time-preferred-times-off')) {
        const date = reqInfo.req.url.split('one-time-preferred-times-off/')[1];
        return this.deleteTemporaryPreferredTimeOff(date);
      }
      if (reqInfo.req.url.includes('ranks')) {
        const date = reqInfo.req.url.split('ranks/')[1];
        return this.deleteEmployeeRank(date);
      }
      if (reqInfo.req.url.includes('capabilities')) {
        const date = reqInfo.req.url.split('capabilities/')[1];
        return this.deleteCapability(date);
      }
      if (reqInfo.req.url.includes('availabilities')) {
        const date = reqInfo.req.url.split('availabilities/')[1];
        return this.deleteAvailabilities(date);
      }
      if (reqInfo.req.url.includes('manager-settings')) {
        const date = reqInfo.req.url.split('manager-settings/')[1];
        return this.deleteManagerSettings(date);
      }
    });
  }

  /**
   * Intercepts HTTP GET request of {@link EmployeeService}. Gets all required records from an in-memory data store.
   * @param reqInfo information about the current HTTP GET request.
   */
  get(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      if (reqInfo.req.url.includes('employees/availabilities')) {
        return this.getAvailabilitiesByEmployee();
      }
      if (reqInfo.req.url.includes('manager-settings')) {
        return this.getGeneralManagerSettings();
      }
      if (reqInfo.req.url.includes('employees/')) {
        const employeeId = reqInfo.req.url.split('employees/')[1].split('/')[0];
        const positionId = reqInfo.req.url.split('positions/')[1];
        return this.getDetails(employeeId, positionId);
      }
      return this.getAll();
    });
  }

  /**
   * Intercepts HTTP POST request of {@link EmployeeService}. Inserts a record to in-memory data store.
   * @param reqInfo information about the current HTTP POST request.
   */
  post(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      const requestBody = reqInfo.utils.getJsonBody(reqInfo.req);
      if (reqInfo.req.url.includes('capabilities')) {
        return this.saveCapabilities(requestBody);
      }
      if (reqInfo.req.url.includes('maximum-work-hours')) {
        return this.saveMaximumWorkingHours(requestBody);
      }
      if (reqInfo.req.url.includes('availabilities')) {
        return this.saveAvailabilities(requestBody);
      }
      if (reqInfo.req.url.includes('one-time-preferred-times-off')) {
        return this.saveTemporaryPreferredTimeOff(requestBody);
      }
      if (reqInfo.req.url.includes('weekly-preferred-times-off')) {
        return this.savePreferredTimeOff(requestBody);
      }
      if (reqInfo.req.url.includes('ranks')) {
        return this.saveEmployeeRank(requestBody);
      }
      if (reqInfo.req.url.includes('manager-settings')) {
        return this.saveManagerSettings(requestBody);
      }
    });
  }

  /**
   * Intercepts HTTP PUT request of {@link EmployeeService}. Updates a record in in-memory data store.
   * @param reqInfo information about the current HTTP PUT request.
   */
  put(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return reqInfo.utils.createResponse$(() => {
      const requestBody = reqInfo.utils.getJsonBody(reqInfo.req);
      if (reqInfo.req.url.includes('availabilities')) {
        const date = reqInfo.req.url.split('availabilities/')[1];
        return this.editAvailabilities(date, requestBody);
      }
      if (reqInfo.req.url.includes('one-time-preferred-times-off')) {
        const date = reqInfo.req.url.split('one-time-preferred-times-off/')[1];
        return this.editTemporaryPreferredTimeOff(date, requestBody);
      }
      if (reqInfo.req.url.includes('weekly-preferred-times-off')) {
        const date = reqInfo.req.url.split('weekly-preferred-times-off/')[1];
        return this.editPreferredTimeOff(date, requestBody);
      }
      if (reqInfo.req.url.includes('maximum-work-hours')) {
        const date = reqInfo.req.url.split('maximum-work-hours/')[1];
        return this.editMaximumWorkingHours(date, requestBody);
      }
      if (reqInfo.req.url.includes('ranks')) {
        const date = reqInfo.req.url.split('ranks/')[1];
        return this.editEmployeeRank(date, requestBody);
      }
      if (reqInfo.req.url.includes('capabilities')) {
        const date = reqInfo.req.url.split('capabilities/')[1];
        return this.editCapabilities(date, requestBody);
      }
      if (reqInfo.req.url.includes('manager-settings')) {
        const date = reqInfo.req.url.split('manager-settings/')[1];
        return this.editManagerSettings(date, requestBody);
      }
    });
  }

  /**
   * Intercepts HTTP PATCH request of {@link EmployeeService}.
   * @param reqInfo information about the current HTTP PATCH request.
   */
  patch(reqInfo: RequestInfo): Observable<ResponseOptions> {
    return undefined;
  }

  private getAll(): ResponseOptions {
    return {
      body: this.testEmployeeDataService.employees,
      status: STATUS.OK
    };
  }

  private getDetails(employeeId: string, positionId: string): ResponseOptions {
    const foundEmployee = this.testEmployeeDataService.employees
      .find(employee => employee.employeeId === employeeId && employee.positionId === positionId);
    return {
      body: {
        positionSummary: foundEmployee,
        capabilities: this.testEmployeeDataService.capabilities,
        maximumWorkingHours: this.testEmployeeDataService.maximumWorkingHours,
        availabilities: this.testEmployeeDataService.availabilities,
        preferredDaysOff: this.testEmployeeDataService.preferredTimesOff,
        temporaryPreferredDaysOff: this.testEmployeeDataService.temporaryPreferredTimesOff,
        employeeRanks: this.testEmployeeDataService.employeeRanks,
        contractHours: this.testEmployeeDataService.getContractHours(foundEmployee),
        managerSettings: this.testEmployeeDataService.managerSettings
      },
      status: STATUS.OK
    };
  }

  private getAvailabilitiesByEmployee(): ResponseOptions {
    const response: StoreEmployeeAvailabilityResponse[] = [{
      employeeId: 'AAA',
      positionId: 'DFSAAA',
      availabilities: [{
        date: '2021-08-23',
        monday: { startTime: '09:00:00', endTime: '17:00:00' },
        tuesday: { startTime: '09:00:00', endTime: '17:00:00' },
        wednesday: { startTime: '09:00:00', endTime: '20:00:00' },
        thursday: null,
        friday: null,
        saturday: { startTime: '09:00:00', endTime: '20:00:00' },
        sunday: { startTime: '09:00:00', endTime: '12:00:00' }
      }]
    }, {
      employeeId: 'BBB',
      positionId: 'DFSBBB',
      availabilities: [{
        date: '2021-08-23',
        monday: { startTime: '09:00:00', endTime: '17:00:00' },
        tuesday: { startTime: '09:00:00', endTime: '17:00:00' },
        wednesday: { startTime: '09:00:00', endTime: '20:00:00' },
        thursday: { startTime: '09:00:00', endTime: '20:00:00' },
        friday: null,
        saturday: null,
        sunday: { startTime: '09:00:00', endTime: '20:00:00' }
      }]
    }, {
      employeeId: 'CCC',
      positionId: 'DFSCCC',
      availabilities: []
    }, {
      employeeId: 'DDD',
      positionId: 'DFSDDD',
      availabilities: []
    }, {
      employeeId: 'EEE',
      positionId: 'DFSEEE',
      availabilities: []
    }, {
      employeeId: 'FFF',
      positionId: 'DFSFFF',
      availabilities: []
    }];
    return {
      body: response,
      status: STATUS.OK
    };
  }

  private saveCapabilities(request: AddEmployeeCapabilityRequest): ResponseOptions {
    this.testEmployeeDataService.capabilities.push({
      date: request.date,
      capabilities: request.capabilities
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editCapabilities(date: string, request: EditEmployeeCapabilityRequest): ResponseOptions {
    const index = this.testEmployeeDataService.capabilities.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.capabilities.splice(index, 1);
    }
    this.testEmployeeDataService.capabilities.push({
      date,
      capabilities: request.capabilities
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteCapability(date: string): ResponseOptions {
    const index = this.testEmployeeDataService.capabilities.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.capabilities.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private saveMaximumWorkingHours(request: AddEmployeeMaximumWorkHoursRequest): ResponseOptions {
    this.testEmployeeDataService.maximumWorkingHours.push({
      date: request.date,
      duration: request.duration
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editMaximumWorkingHours(date: string, request: EditEmployeeMaximumWorkHoursRequest): ResponseOptions {
    const index = this.testEmployeeDataService.maximumWorkingHours.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.maximumWorkingHours.splice(index, 1);
    }
    this.testEmployeeDataService.maximumWorkingHours.push({
      date,
      duration: request.duration
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteMaximumWorkingHours(date: string): ResponseOptions {
    const index = this.testEmployeeDataService.maximumWorkingHours.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.maximumWorkingHours.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private saveAvailabilities(request: AddEmployeeAvailabilityRequest): ResponseOptions {
    this.testEmployeeDataService.availabilities.push({
      date: request.date,
      monday: { startTime: request?.monday?.startTime, endTime: request?.monday?.endTime },
      tuesday: { startTime: request?.tuesday?.startTime, endTime: request?.tuesday?.endTime },
      wednesday: { startTime: request?.wednesday?.startTime, endTime: request?.wednesday?.endTime },
      thursday: { startTime: request?.thursday?.startTime, endTime: request?.thursday?.endTime },
      friday: { startTime: request?.friday?.startTime, endTime: request?.friday?.endTime },
      saturday: { startTime: request?.saturday?.startTime, endTime: request?.saturday?.endTime },
      sunday: { startTime: request?.sunday?.startTime, endTime: request?.sunday?.endTime }
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editAvailabilities(date: string, request: EditEmployeeAvailabilityRequest): ResponseOptions {
    const index = this.testEmployeeDataService.availabilities.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.availabilities.splice(index, 1);
    }
    this.testEmployeeDataService.availabilities.push({
      date,
      monday: { startTime: request?.monday?.startTime, endTime: request?.monday?.endTime },
      tuesday: { startTime: request?.tuesday?.startTime, endTime: request?.tuesday?.endTime },
      wednesday: { startTime: request?.wednesday?.startTime, endTime: request?.wednesday?.endTime },
      thursday: { startTime: request?.thursday?.startTime, endTime: request?.thursday?.endTime },
      friday: { startTime: request?.friday?.startTime, endTime: request?.friday?.endTime },
      saturday: { startTime: request?.saturday?.startTime, endTime: request?.saturday?.endTime },
      sunday: { startTime: request?.sunday?.startTime, endTime: request?.sunday?.endTime }
    });

    return {
      status: STATUS.OK
    };
  }

  private deleteAvailabilities(date: string): ResponseOptions {
    const index = this.testEmployeeDataService.availabilities.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.availabilities.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private saveTemporaryPreferredTimeOff(request: AddEmployeeOneTimePreferredTimeOffRequest): ResponseOptions {
    this.testEmployeeDataService.temporaryPreferredTimesOff.push({
      date: request.date,
      preferredTimeOff: {
        startTime: request.startTime,
        endTime: request.endTime,
        reason: request.reason
      }
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editTemporaryPreferredTimeOff(date: string, request: EditEmployeeOneTimePreferredTimeOffRequest): ResponseOptions {
    const index = this.testEmployeeDataService.temporaryPreferredTimesOff.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.temporaryPreferredTimesOff.splice(index, 1);
    }
    this.testEmployeeDataService.temporaryPreferredTimesOff.push({
      date,
      preferredTimeOff: {
        startTime: request.startTime,
        endTime: request.endTime,
        reason: request.reason
      }
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteTemporaryPreferredTimeOff(date: string): ResponseOptions {
    const index = this.testEmployeeDataService.temporaryPreferredTimesOff.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.temporaryPreferredTimesOff.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private savePreferredTimeOff(request: AddEmployeeWeeklyPreferredTimeOffRequest): ResponseOptions {
    this.testEmployeeDataService.preferredTimesOff.push({
      date: request.date,
      monday: {
        startTime: request?.monday?.startTime,
        endTime: request?.monday?.endTime,
        reason: request?.monday?.reason
      },
      tuesday: {
        startTime: request?.tuesday?.startTime,
        endTime: request?.tuesday?.endTime,
        reason: request?.tuesday?.reason
      },
      wednesday: {
        startTime: request?.wednesday?.startTime,
        endTime: request?.wednesday?.endTime,
        reason: request?.wednesday?.reason
      },
      thursday: {
        startTime: request?.thursday?.startTime,
        endTime: request?.thursday?.endTime,
        reason: request?.thursday?.reason
      },
      friday: {
        startTime: request?.friday?.startTime,
        endTime: request?.friday?.endTime,
        reason: request?.friday?.reason
      },
      saturday: {
        startTime: request?.saturday?.startTime,
        endTime: request?.saturday?.endTime,
        reason: request?.saturday?.reason
      },
      sunday: {
        startTime: request?.sunday?.startTime,
        endTime: request?.sunday?.endTime,
        reason: request?.sunday?.reason
      }
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editPreferredTimeOff(date: string, request: EditEmployeeWeeklyPreferredTimeOffRequest): ResponseOptions {
    const index = this.testEmployeeDataService.preferredTimesOff.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.preferredTimesOff.splice(index, 1);
    }
    this.testEmployeeDataService.preferredTimesOff.push({
      date,
      monday: {
        startTime: request?.monday?.startTime,
        endTime: request?.monday?.endTime,
        reason: request?.monday?.reason
      },
      tuesday: {
        startTime: request?.tuesday?.startTime,
        endTime: request?.tuesday?.endTime,
        reason: request?.tuesday?.reason
      },
      wednesday: {
        startTime: request?.wednesday?.startTime,
        endTime: request?.wednesday?.endTime,
        reason: request?.wednesday?.reason
      },
      thursday: {
        startTime: request?.thursday?.startTime,
        endTime: request?.thursday?.endTime,
        reason: request?.thursday?.reason
      },
      friday: {
        startTime: request?.friday?.startTime,
        endTime: request?.friday?.endTime,
        reason: request?.friday?.reason
      },
      saturday: {
        startTime: request?.saturday?.startTime,
        endTime: request?.saturday?.endTime,
        reason: request?.saturday?.reason
      },
      sunday: {
        startTime: request?.sunday?.startTime,
        endTime: request?.sunday?.endTime,
        reason: request?.sunday?.reason
      }
    });

    return {
      status: STATUS.OK
    };
  }

  private saveEmployeeRank(request: AddEmployeeRankRequest): ResponseOptions {
    this.testEmployeeDataService.employeeRanks.push({
      date: request.date,
      rank: request.rank
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editEmployeeRank(date: string, request: EditEmployeeRankRequest): ResponseOptions {
    const index = this.testEmployeeDataService.employeeRanks.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.employeeRanks.splice(index, 1);
    }
    this.testEmployeeDataService.employeeRanks.push({
      date,
      rank: request.rank
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteEmployeeRank(date: string): ResponseOptions {
    const index = this.testEmployeeDataService.employeeRanks.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.employeeRanks.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }

  private getGeneralManagerSettings(): ResponseOptions {
    return {
      body: this.testEmployeeDataService.managerSettings,
      status: STATUS.OK
    };
  }

  private saveManagerSettings(request: AddEmployeeManagerSettingsRequest): ResponseOptions {
    this.testEmployeeDataService.managerSettings.push({
      date: request.date,
      canDoSales: request.canDoSales
    });

    return {
      status: STATUS.CREATED
    };
  }

  private editManagerSettings(date: string, request: EditEmployeeManagerSettingsRequest): ResponseOptions {
    const index = this.testEmployeeDataService.managerSettings.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.managerSettings.splice(index, 1);
    }
    this.testEmployeeDataService.managerSettings.push({
      date,
      canDoSales: request.canDoSales
    });

    return {
      status: STATUS.CREATED
    };
  }

  private deleteManagerSettings(date: string): ResponseOptions {
    const index = this.testEmployeeDataService.managerSettings.findIndex(item => item.date === date);
    if (index !== -1) {
      this.testEmployeeDataService.managerSettings.splice(index, 1);
    }

    return {
      status: STATUS.OK
    };
  }
}
